import { z } from 'zod';

import { UuidSchema } from '../../../common/id/id';
import { PermissionEffectSchema } from '../../permissions/permission-effect';
import { PermissionKeySchema } from '../../permissions/permission-key/permission-key';

/** One direct grant or deny, as the reviewed save carries it. */
const ReviewedDirectPermissionSchema = z
  .object({
    permissionKey: PermissionKeySchema,
    effect: PermissionEffectSchema,
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .strict();

/**
 * One staff member's whole access, saved in **one** reviewed transaction.
 *
 * It replaced — and, since P10.6i, is the **only** thing that can write this
 * account's access — the sequential loop of `PUT roles` plus one request per
 * grant, deny and clear, which tolerated partial failure by design (N28).
 *
 * **There is no `clears` list, deliberately.** Both collections are the complete
 * set, so clearing a carve-out is *leaving it out* — idempotent, and free of the
 * lost-update race a delta has when two operators edit the same account. It is
 * the argument a whole-set role assignment already makes, applied to the other
 * half of the same screen.
 *
 * A **repeated `permissionKey` is refused** rather than resolved last-one-wins:
 * one key cannot be granted and denied at once (the `(user, permission)` unique
 * index says so), and two rows for one key is a client bug that silently picking
 * a winner would hide. The refusal carries an English constant, so a client
 * renders its own translated sentence for it (U8).
 */
export const ReviewedAccessSaveSchema = z
  .object({
    roleIds: z.array(UuidSchema).max(20),
    permissions: z.array(ReviewedDirectPermissionSchema).max(200),
  })
  .strict()
  .refine(
    (value) =>
      new Set(value.permissions.map((entry) => entry.permissionKey)).size ===
      value.permissions.length,
    {
      path: ['permissions'],
      message: 'Each permission key may appear once — a key cannot be both granted and denied.',
    },
  );

export type ReviewedAccessSaveRequest = z.infer<typeof ReviewedAccessSaveSchema>;

/** One entry of the saved direct set. */
export type ReviewedDirectPermission = z.infer<typeof ReviewedDirectPermissionSchema>;
